import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { BixiI18nService, IFilterI18n } from '@bixi/core/i18n';
import { getLocalItem, setLocalItem } from '@bixi/core/internal';
import { get } from 'lodash-es';
import { isNil } from 'lodash-es';
import { NzSafeAny } from 'ng-zorro-antd/core/types';
import { interval, Subject, Subscription } from 'rxjs';
import { debounce, filter } from 'rxjs/operators';
import { BixiFilterService } from './filter.service';
import { EFilterType, EStrategy, IFilterCol, IFilterKeywordPlacement, IFilterOptions, IFilterParams } from './filter.type';

interface ISearchParamsChangeWithStrategy {
  mode: EStrategy;
  type: EFilterType;
  value: IFilterParams;
}

@Component({
  selector: 'bixi-filter',
  exportAs: 'bixiFilter',
  template: `
    <div class="bixi-filter">
      <div class="bixi-filter-header">
        <div class="bixi-filter-header-left-wrap">
          <ng-content select="bixi-filter-actions,[bixi-filter-actions]"></ng-content>
        </div>
        <div class="bixi-filter-header-right">
          <nz-input-group class="keyword-text" *ngIf="showKeyword && keywordPlacement !== 'inner'" [nzSuffix]="suffixIconSearch">
            <input
              nz-input
              [placeholder]="keywordPlaceholder ? keywordPlaceholder : localeTranslation.placeholder"
              [(ngModel)]="filterParams[keyword]"
              (keyup.enter)="onKeywordChange(updateMode.onEnter)"
              (blur)="onKeywordChange(updateMode.onBlur)"
              (ngModelChange)="onKeywordChange(updateMode.normal)"
            />
          </nz-input-group>
          <ng-template #suffixIconSearch>
            <i nz-icon nzType="search" [ngStyle]="{ cursor: 'pointer' }" (click)="onKeywordChange(updateMode.onSearchIconClick)"></i>
          </ng-template>
          <div
            *ngIf="(options && options.length) || content"
            [ngClass]="{ 'bixi-filter-advance-button': true, 'bixi-filter-advance-button-active': advancePanelActive }"
            nz-popover
            nzPopoverTrigger="click"
            [nzPopoverTitle]="content ? content : contentTemplate"
            [nzPopoverContent]="footer ? footer : footerTemplate"
            [(nzPopoverVisible)]="advancePanelOpen"
            nzPopoverPlacement="bottomRight"
          >
            <i nz-icon nzType="filter" nzTheme="outline"></i>{{ localeTranslation.filter }}
            <span class="point" *ngIf="advancePanelActive"></span>
          </div>
          <bixi-filter-keys [cols]="cols" *ngIf="cols.length" (filterColsChange)="onKeysChange($event)"></bixi-filter-keys>
        </div>
      </div>
      <ng-template #contentTemplate>
        <bixi-filter-advance
          [(filterParams)]="filterParams"
          [options]="options"
          (searchChange)="onSearchChange($event)"
        ></bixi-filter-advance>
      </ng-template>
      <ng-template #footerTemplate>
        <span
          (click)="onReset()"
          [class.bixi-filter-footer-active]="advancePanelActive"
          [class.bixi-filter-footer-normal]="!advancePanelActive"
          [ngStyle]="{ cursor: 'pointer' }"
          ><i nz-icon nzType="undo" nzTheme="outline"></i> {{ localeTranslation.reset }}
        </span>
      </ng-template>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BixiFilterComponent implements OnChanges, OnDestroy, OnInit {
  keyList: string[] = [];
  _advancePanelActive = false;
  advancePanelOpen = false;
  filterParams$ = new Subject<ISearchParamsChangeWithStrategy>();
  updateMode = EStrategy;
  normalStrategySubscription = new Subscription();
  onEnterStrategySubscription = new Subscription();
  onBlurStrategySubscription = new Subscription();
  onSearchIconStrategySubscription = new Subscription();
  i18nSubscription = new Subscription();
  nonTextSubscription = new Subscription();

  get advancePanelActive(): boolean {
    const ret = { ...this.filterParams };
    for (const key in ret) {
      if (ret.hasOwnProperty(key)) {
        if (!this.keyList.includes(key)) {
          delete ret[key];
        }
        if (this.keyList.includes(key)) {
          const val = ret[key];
          if (val === '' || (Array.isArray(val) && val.length === 0) || (Array.isArray(val) && val[0] === null && val[1] === null)) {
            delete ret[key];
          }
        }
      }
    }
    if (!this.advancePanelOpen) {
      this._advancePanelActive = Object.keys(ret).length !== 0;
    }
    return this._advancePanelActive;
  }

  _cols: IFilterCol[] = [];
  localeTranslation: IFilterI18n;

  @Input() options: IFilterOptions = [];
  @Input() filterParams: IFilterParams = {};
  @Input() content: TemplateRef<void>;
  @Input() footer: TemplateRef<void>;
  @Input() showKeyword = false; // 是否显示 keyword
  @Input() keyword = 'keyword'; // filterParams 中的 key
  @Input() keywordPlacement: IFilterKeywordPlacement = 'outer'; // 显示位置
  @Input() keywordPlaceholder: string;
  @Input() key = 'keys'; // 用于在 localstorage 存储 filter keys。
  @Input() strategies: EStrategy[] = [EStrategy.normal];
  @Input() debounceTime = 0;
  @Output() colsChange = new EventEmitter<IFilterCol[]>();
  @Output() filterParamsChange = new EventEmitter<IFilterParams>();
  @Output() search = new EventEmitter<IFilterParams>();

  @Input()
  get cols() {
    return this._cols;
  }

  set cols(value: IFilterCol[]) {
    this._cols = value.map(col => {
      return {
        ...col,
        ...(col.filterOptions || {})
      };
    });
  }

  constructor(private filterService: BixiFilterService, private i18n: BixiI18nService, private cdr: ChangeDetectorRef) {
    setTimeout(() => {
      if (this.cols.length) {
        this.cols = this.mergeWithCache(this.cols);
        this.colsChange.emit(this.cols);
      }
    });

    this.normalStrategySubscription = this.filterParams$
      .pipe(
        filter(e => e.type === EFilterType.text && e.mode === EStrategy.normal),
        debounce(() => interval(this.debounceTime))
      )
      .subscribe(res => {
        this.search.emit(res.value);
      });

    this.nonTextSubscription = this.filterParams$.pipe(filter(e => e.type !== EFilterType.text)).subscribe(res => {
      this.search.emit(res.value);
    });
  }

  ngOnInit() {
    if (!this.localeTranslation) {
      this.i18nSubscription = this.i18n.localeChange.subscribe(() => this.setTranslation());
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const { options, strategies, debounceTime } = changes;

    if (options) {
      this.options = this.makeOptions(options.currentValue);
    }
    this.keyList = this.options.map(e => e.key);

    if (strategies) {
      const _strategies = get(strategies, 'currentValue', []) as EStrategy[];
      this.unSubscribeAll();
      _strategies.forEach(m => {
        switch (m) {
          case EStrategy.normal:
            this.normalStrategySubscription = this.filterParams$
              .pipe(
                filter(e => e.type === EFilterType.text && e.mode === EStrategy.normal),
                debounce(() => interval(this.debounceTime))
              )
              .subscribe(res => {
                this.search.emit(res.value);
              });
            break;
          case EStrategy.onBlur:
            this.onBlurStrategySubscription = this.filterParams$
              .pipe(filter(e => e.type === EFilterType.text && e.mode === EStrategy.onBlur))
              .subscribe(res => {
                this.search.emit(res.value);
              });
            break;
          case EStrategy.onEnter:
            this.onEnterStrategySubscription = this.filterParams$
              .pipe(filter(e => e.type === EFilterType.text && e.mode === EStrategy.onEnter))
              .subscribe(res => {
                this.search.emit(res.value);
              });
            break;
          case EStrategy.onSearchIconClick:
            this.onSearchIconStrategySubscription = this.filterParams$
              .pipe(filter(e => e.type === EFilterType.text && e.mode === EStrategy.onSearchIconClick))
              .subscribe(res => {
                this.search.emit(res.value);
              });
            break;
        }
      });
      if (_strategies.length > 0) {
        this.nonTextSubscription.unsubscribe();
        this.nonTextSubscription = this.filterParams$.pipe(filter(e => e.type !== EFilterType.text)).subscribe(res => {
          this.search.emit(res.value);
        });
      }
    }

    if (!isNil(debounceTime)) {
      this.normalStrategySubscription.unsubscribe();
      this.normalStrategySubscription = this.filterParams$
        .pipe(
          filter(e => e.type === EFilterType.text && e.mode === EStrategy.normal),
          debounce(() => interval(debounceTime.currentValue))
        )
        .subscribe(res => {
          this.search.emit(res.value);
        });
    }
  }

  onKeysChange(value: IFilterCol[]) {
    setLocalItem(this.key, value);
    this.cols = [...value];
    this.colsChange.emit(this.cols);
  }

  onSearchChange(value: ISearchParamsChangeWithStrategy) {
    this.filterParams$.next(value);
    this.filterParamsChange.emit(this.filterParams);
  }

  onKeywordChange(mode: EStrategy) {
    this.filterParams$.next({ mode, type: EFilterType.text, value: this.filterParams });
    this.filterParamsChange.emit(this.filterParams);
  }

  onReset() {
    const ret = { ...this.filterParams };
    this.options
      .map(e => e.key)
      .forEach(e => {
        delete ret[e];
      });
    this.filterParams = ret;
    this.filterParamsChange.emit(this.filterParams);
    this.search.emit(this.filterParams);
  }

  mergeWithCache(nextCols: IFilterCol[]) {
    const cacheCols: NzSafeAny[] = getLocalItem(this.key, []);
    if (!Array.isArray(cacheCols)) return nextCols;
    // 删除缓存中多余列
    let deletedAndCompletedCache = Array.from(cacheCols, cacheCol => {
      const cahceAlias = cacheCol.keyAlias || cacheCol.key;
      const exist = nextCols.find(col => {
        const colAlias = col.keyAlias || col.key;
        if (cahceAlias === colAlias) {
          cacheCol = { ...col, visible: cacheCol.visible };
          return true;
        }
        return false;
      });
      if (exist) return cacheCol;
      return null;
    });

    deletedAndCompletedCache = deletedAndCompletedCache.filter(col => col);

    // 以缓存中列排序和 visible 为准，插入新增内容
    Array.from(nextCols, (col, index) => {
      const _colAlias = col.keyAlias || col.key;
      const exist = deletedAndCompletedCache.find(cacheCol => {
        const cacheColAlias = cacheCol.keyAlias || cacheCol.key;
        return cacheColAlias === _colAlias;
      });
      if (!exist) {
        deletedAndCompletedCache.splice(index, 0, col);
      }
    });

    return deletedAndCompletedCache;
  }

  private setTranslation() {
    this.localeTranslation = this.i18n.getLocaleData('filter') || {};
    this.cdr.markForCheck();
  }

  private makeOptions(options: IFilterOptions): IFilterOptions {
    const keywordText = {
      type: EFilterType.text,
      placeholder: this.keywordPlaceholder,
      key: this.keyword
    };
    const mergeKeyword = this.showKeyword && this.keywordPlacement !== 'outer';
    const os = this.filterService.genOptions(options);
    if (mergeKeyword) {
      return [keywordText, ...os] as IFilterOptions;
    }
    return os;
  }

  private unSubscribeAll() {
    this.normalStrategySubscription.unsubscribe();
    this.onSearchIconStrategySubscription.unsubscribe();
    this.onEnterStrategySubscription.unsubscribe();
    this.onBlurStrategySubscription.unsubscribe();
    this.nonTextSubscription.unsubscribe();
    this.i18nSubscription.unsubscribe();
  }

  ngOnDestroy() {
    this.unSubscribeAll();
  }
}
